import { BASE_URL } from "utils/constant";

export function getStatusBadgeClass(status) {
  switch (status) {
    case "Approved":
      return "bg-green-100 text-green-800";
    case "Locked":
      return "bg-blue-100 text-blue-800";
    case "Under Review":
      return "bg-yellow-100 text-yellow-800";
    case "Pending":
      return "bg-gray-100 text-gray-800";
    case "Open":
      return "bg-yellow-100 text-yellow-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
}

function getFileExtensionFromUrl(url) {
  const u = String(url || "");
  if (u.startsWith("https://")) {
    const filePart = u.split("?")[0];
    const last = filePart.split(".").pop().toLowerCase();
    return last;
  }
  return u.split(".").pop().toLowerCase();
}

function normalizeFileUrl(maybeUrl) {
  if (!maybeUrl) return null;
  const u = String(maybeUrl);
  if (
    u.startsWith("http://") ||
    u.startsWith("https://") ||
    u.startsWith("data:") ||
    u.startsWith("blob:")
  ) {
    return u;
  }
  // Many backends return a relative media path like "/media/..." – prefix API base.
  return `${BASE_URL}${u.startsWith("/") ? "" : "/"}${u}`;
}

function normalizeDesignStatus(raw) {
  const s = String(raw || "").trim().toLowerCase();
  if (!s) return "Pending";
  if (s === "approved") return "Approved";
  if (s === "locked") return "Locked";
  if (s === "under review" || s === "under_review" || s === "under-review") return "Under Review";
  // backend sometimes uses draft/pending for not-yet-approved
  if (s === "draft" || s === "pending") return "Draft";
  // fallback: Title Case-ish
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export async function downloadDiagram(url, suggestedName = "design") {
  if (!url) {
    alert("No file available to download");
    return;
  }

  try {
    // data: or blob: urls can be downloaded directly
    if (url.startsWith("data:") || url.startsWith("blob:")) {
      const a = document.createElement("a");
      a.href = url;
      a.download = suggestedName.replace(/\s+/g, "_");
      document.body.appendChild(a);
      a.click();
      a.remove();
      return;
    }

    const accessToken = localStorage.getItem("access");
    // If the URL already contains a SAS token (common with Azure blobs: sig=, sv=, se=),
    // do NOT include an Authorization header — it will cause authentication failures (403).
    const looksLikeSas = url.includes("sig=") || url.includes("sv=") || url.includes("se=") || url.includes("skoid=") || url.includes("sktid=") || url.includes("skt=");
    const headers = {};
    if (!looksLikeSas && accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }
    const res = await fetch(url, { headers });

    if (!res.ok) throw new Error("Network response was not ok");
    const blob = await res.blob();

    // Determine filename
    let filename = suggestedName.replace(/\s+/g, "_");
    const contentDisposition = res.headers.get("content-disposition") || "";
    const contentType = res.headers.get("content-type") || "";
    const match = contentDisposition.match(/filename="?([^"]+)"?/);
    if (match && match[1]) {
      filename = match[1];
    } else {
      const ext = (contentType.split("/").pop() || "").split(";")[0];
      if (ext) filename = `${filename}.${ext}`;
      else {
        const parts = url.split("/");
        const last = parts[parts.length - 1] || "";
        filename = decodeURIComponent(last.split("?")[0] || filename);
      }
    }

    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.error("Download failed", err);
    alert("Download failed");
  }
}

export async function fetchCommentsForDesign(designId) {
  if (!designId) return [];
  try {
    const accessToken = localStorage.getItem("access");
    const res = await fetch(`${BASE_URL}/design/designs/${designId}/comments/`, {
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.detail || err?.message || "Failed to fetch comments");
    }
    const json = await res.json();
    // API may return array directly or { results: [...] }
    return Array.isArray(json) ? json : (json?.results || json?.data || []);
  } catch (err) {
    console.error("fetchCommentsForDesign failed", err);
    return [];
  }
}

export async function createCommentForDesign(designId, commentText) {
  if (!designId || !commentText) throw new Error("Invalid input");
  const accessToken = localStorage.getItem("access");
  const body = { comment: commentText, design: designId };
  const res = await fetch(`${BASE_URL}/design/designs/comments/create/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.detail || err?.message || "Failed to create comment");
  }
  const json = await res.json();
  return json;
}

export async function fetchPinsForDesign(designId) {
  if (!designId) return [];
  try {
    const accessToken = localStorage.getItem("access");
    const res = await fetch(`${BASE_URL}/design/designs/${designId}/pins/`, {
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.detail || err?.message || "Failed to fetch pins");
    }
    const json = await res.json().catch(() => ({}));
    return Array.isArray(json) ? json : (json?.data || []);
  } catch (err) {
    console.error("fetchPinsForDesign failed", err);
    return [];
  }
}

export async function createPinForDesign(designId, pin) {
  if (!designId) throw new Error("Invalid design id");
  if (!pin) throw new Error("Invalid pin input");
  const accessToken = localStorage.getItem("access");

  const body = {
    design: designId,
    x_coordinate: pin.x_coordinate,
    y_coordinate: pin.y_coordinate,
    comment: pin.comment,
    ...(typeof pin.assigned_to !== "undefined" && pin.assigned_to !== null
      ? { assigned_to: pin.assigned_to }
      : {}),
    ...(pin.assignees && Array.isArray(pin.assignees) ? { assignees: pin.assignees } : {}),
  };

  const res = await fetch(`${BASE_URL}/design/designs/pins/create/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify(body),
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json?.detail || json?.message || "Failed to create pin");
  }
  return json;
}

export async function updatePinById(pinId, payload) {
  if (!pinId) throw new Error("Invalid pin id");
  const accessToken = localStorage.getItem("access");

  const res = await fetch(`${BASE_URL}/design/designs/pins/${pinId}/update/`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify(payload),
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json?.detail || json?.message || "Failed to update pin");
  }
  return json;
}

export async function resolvePinById(pinId, payload) {
  if (!pinId) throw new Error("Invalid pin id");
  const accessToken = localStorage.getItem("access");
  const res = await fetch(`${BASE_URL}/design/pins/${pinId}/resolve/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify(payload),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json?.detail || json?.message || "Failed to resolve pin");
  }
  return json;
}

export async function deletePinById(pinId) {
  if (!pinId) throw new Error("Invalid pin id");
  const accessToken = localStorage.getItem("access");
  const res = await fetch(`${BASE_URL}/design/designs/pins/${pinId}/delete/`, {
    method: "DELETE",
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.detail || err?.message || "Failed to delete pin");
  }
  return true;
}

export async function deleteCommentById(commentId) {
  if (!commentId) throw new Error("Invalid comment id");
  const accessToken = localStorage.getItem("access");
  const res = await fetch(`${BASE_URL}/design/designs/comments/${commentId}/delete/`, {
    method: "DELETE",
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.detail || err?.message || "Failed to delete comment");
  }
  return true;
}

export async function getDesignsByOrg(orgId) {
  const accessToken = localStorage.getItem("access");
  const res = await fetch(`${BASE_URL}/design/designs/org/${orgId}/`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  const json = await res.json();
  const list = Array.isArray(json) ? json : (json?.data || []);
  return list.map(item => ({
    id: item.id,
    name: item.design_name || "Untitled Design",
    site: item.site,
    projectName: item.site_name || "Unknown Project",
    version_number: item.version_number,
    original_version: item.version_number,
    status: normalizeDesignStatus(item.status),
    lastUpdated: item.last_updated_on?.split("T")[0] || new Date().toISOString().split("T")[0],
    approverName: item.approver_name || item.approverName || "Pending",
    fileType: item.type || "Layout",
    originalType: item.type,
    uploadedBy: item.created_by || "Unknown",
    originalUploadedBy: item.created_by,
    diagram: normalizeFileUrl(item.diagram),
    originalDiagram: item.diagram,
    file_type: item.file_type,
    originalFileType: item.file_type,
    urn: item.aps_urn,
    aps_status: item.aps_status,
    // Optional progress (if backend exposes it). Kept flexible because field naming varies.
    aps_progress:
      item.aps_progress ??
      item.aps_percentage ??
      item.aps_percent ??
      item.progress ??
      item.percentage ??
      item.percent ??
      null,
    originalUrn: item.aps_urn,
    assignee: item.members || [],
    linkedTask: undefined,
    linkedTasks: [],
    versions: item.versions || [],
    pins: item.pins || [],
    comments: item.comments || [],
  }));
}

export async function getDesignById(designId) {
  if (!designId && designId !== 0) throw new Error("Invalid design id");
  const accessToken = localStorage.getItem("access");
  const res = await fetch(`${BASE_URL}/design/designs/${designId}/`, {
    method: "GET",
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json?.detail || json?.message || "Failed to fetch design details");
  }

  const item = json?.data && typeof json?.success !== "undefined" ? json.data : json;
  return {
    ...item,
    id: item.id || item.pk || String(item.id || designId),
    name: item.design_name || item.name || "Untitled Design",
    original_version: item.version_number,
    version_number: item.version_number,
    site: item.site,
    projectName: item.site_name || item.site?.site_name || String(item.site || "Unknown Project"),
    status: normalizeDesignStatus(item.status),
    lastUpdated: item.last_updated_on?.split("T")[0] || new Date().toISOString().split("T")[0],
    approverName: item.approver_name || item.approverName || "Pending",
    fileType: item.type || "Layout",
    originalType: item.type,
    uploadedBy: item.created_by || "Unknown",
    originalUploadedBy: item.created_by,
    diagram: normalizeFileUrl(item.diagram),
    originalDiagram: item.diagram,
    file_type: item.file_type,
    originalFileType: item.file_type,
    urn: item.aps_urn,
    originalUrn: item.aps_urn,
    aps_status: item.aps_status,
    aps_progress:
      item.aps_progress ??
      item.aps_percentage ??
      item.aps_percent ??
      item.progress ??
      item.percentage ??
      item.percent ??
      null,
    assignee: item.members || [],
    linkedTask: undefined,
    linkedTasks: item.linkedTasks || [],
    versions: item.versions || [],
    pins: item.pins || [],
    comments: item.comments || [],
  };
}

export async function linkDesignToTasks(designId, taskIds = []) {
  if (!designId && designId !== 0) throw new Error("Invalid design id");
  const accessToken = localStorage.getItem("access");
  const res = await fetch(
    `${BASE_URL}/design/designs/${designId}/link-to-tasks/`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify({ task_ids: taskIds }),
    }
  );
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(
      json?.detail || json?.message || "Failed to link design to tasks"
    );
  }
  return json;
}

export async function fetchLinkedTasks(designId) {
  if (!designId && designId !== 0) throw new Error("Invalid design id");
  const accessToken = localStorage.getItem("access");
  const res = await fetch(`${BASE_URL}/design/designs/${designId}/get-linked-tasks/`, {
    method: "GET",
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json?.detail || json?.message || "Failed to fetch linked tasks");
  }
  return json?.tasks || [];
}

export async function replaceDesignFile(designId, file) {
  if (!designId) throw new Error("Invalid design id");
  if (!file) throw new Error("No file selected");
  const accessToken = localStorage.getItem("access");
  const formPayload = new FormData();
  // backend expects `diagram` (same as create)
  formPayload.append("diagram", file);

  const res = await fetch(`${BASE_URL}/design/designs/${designId}/replace/`, {
    method: "POST",
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    body: formPayload,
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json?.detail || json?.message || "Failed to replace file");
  }
  return json;
}

export async function approveDesignById(designId) {
  if (!designId) throw new Error("Invalid design id");
  const accessToken = localStorage.getItem("access");
  const res = await fetch(`${BASE_URL}/design/designs/${designId}/approve/`, {
    method: "POST",
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json?.detail || json?.message || "Failed to approve design");
  }
  return json;
}

export async function lockDesignById(designId) {
  if (!designId) throw new Error("Invalid design id");
  const accessToken = localStorage.getItem("access");
  const res = await fetch(`${BASE_URL}/design/designs/${designId}/lock/`, {
    method: "POST",
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json?.detail || json?.message || "Failed to lock design");
  }
  return json;
}

export async function reopenDesign(designId) {
  if (!designId) throw new Error("Invalid design id");
  const accessToken = localStorage.getItem("access");
  const res = await fetch(`${BASE_URL}/design/designs/${designId}/reopen/`, {
    method: "POST",
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json?.detail || json?.message || "Failed to reopen design");
  }
  return json;
}

export async function fetchDesignVersions(designId) {
  if (!designId) return [];
  const accessToken = localStorage.getItem("access");
  const res = await fetch(`${BASE_URL}/design/designs/${designId}/versions/`, {
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json?.detail || json?.message || "Failed to fetch version history");
  }
  const list = Array.isArray(json) ? json : (json?.results || json?.data || []);

  // Normalize into a UI-friendly shape (but keep unknown fields for flexibility)
  return (list || []).map((v, idx) => {

    const uploadedBy = v.uploaded_by || "Unknown";

    const uploadDateRaw = v.upload_date;
    const uploadDate = uploadDateRaw ? String(uploadDateRaw).split("T")[0] : "";

    const status = normalizeDesignStatus(v.status);

    const fileUrlRaw = v.file || null;
    const fileUrl = normalizeFileUrl(fileUrlRaw);

    return {
      id: v.id || v.pk || `${designId}-v-${idx}`,
      version: v.version,
      name: v.name || "Design File",
      fileType: v.file_type || getFileExtensionFromUrl(fileUrl),
      aps_urn: v.aps_urn || null,
      uploadedBy,
      uploadDate,
      status,
      fileUrl,
    };
  });
}

export async function deleteDesignById(designId) {
  const accessToken = localStorage.getItem("access");
  const res = await fetch(`${BASE_URL}/design/designs/${designId}/delete/`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.detail || err?.message || "Failed to delete design");
  }
  return true;
}

export async function fetchDesignMembers(designId) {
  if (!designId) return [];
  try {
    const accessToken = localStorage.getItem("access");
    const res = await fetch(`${BASE_URL}/design/designs/members/${designId}/`, {
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(json?.detail || json?.message || "Failed to fetch design members");
    }
    return Array.isArray(json) ? json : (json?.results || json?.data || []);
  } catch (err) {
    console.error("fetchDesignMembers failed", err);
    return [];
  }
}

export async function addMemberToDesign(designId, memberId) {
  if (!designId) throw new Error("Invalid design id");
  if (!memberId && memberId !== 0) throw new Error("Invalid member id");

  const accessToken = localStorage.getItem("access");
  const res = await fetch(`${BASE_URL}/design/designs/members/${designId}/create/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify({ member_id: Number(memberId) }),
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json?.detail || json?.message || "Failed to add design member");
  }
  return json;
}

export async function updateDesignMembers(designId, memberIds) {
  if (!designId) throw new Error("Invalid design id");
  if (!Array.isArray(memberIds)) throw new Error("memberIds must be an array");

  const accessToken = localStorage.getItem("access");
  const res = await fetch(`${BASE_URL}/design/designs/members/${designId}/update/`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify({ member_ids: memberIds }),
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json?.detail || json?.message || "Failed to update design members");
  }
  return json;
}

export async function fetchOrganizationMembers(organizationId) {
  if (!organizationId) return [];
  try {
    const accessToken = localStorage.getItem("access");
    const res = await fetch(`${BASE_URL}/org/organization/${organizationId}/members/`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(json?.detail || json?.message || "Failed to fetch organization members");
    }
    // org endpoints in this repo commonly return { status, data: [...] }
    const list = Array.isArray(json) ? json : (json?.data || []);
    return list;
  } catch (err) {
    console.error("fetchOrganizationMembers failed", err);
    return [];
  }
}

export async function getDesignApsStatus(designId) {
  if (!designId && designId !== 0) throw new Error("Invalid design id");
  const accessToken = localStorage.getItem("access");
  const res = await fetch(`${BASE_URL}/design/designs/${designId}/status/`, {
    method: "GET",
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json?.detail || json?.message || "Failed to fetch design status");
  }
  return json;
}